// Kerangka bersama tab Cataloguer FORM02 (New_*Tab.vue di folder ini, dirender New_CataloguerForm).
//
// SATU objek item: semua tab membaca & menulis ke prop `mcrItem` (= store mcr.selectedMcrItem, objek yang sama
// yang dibaca New_McrFooter.saveCatalog). `dialog.form` selalu === `mcrItem`. JANGAN cari ulang item by id di
// mcr.allMcrItem lalu menulis ke hasilnya -- itu bisa objek lain, perubahan user tidak ikut ter-Save
// (lihat DECISIONS_LOG.md "tab FORM02 WAJIB menulis ke objek prop mcrItem").
//
// Pemakaian di tab:
//   import { cataloguerTabMixin, useCataloguerTabEngine, createTabDialog } from "./New_CataloguerTab";
//   export default {
//     mixins: [cataloguerTabMixin],
//     setup(props) { return useCataloguerTabEngine(props, (vm) => ({ fieldResolvers: { ... } })); },
//     data() { return { MY_TAB, dialog: createTabDialog(this.mcrItem, { extraState: ... }) }; },
//     methods: { onItemBound() { /* logic khusus tab saat item dipilih/berganti */ } }
//   };
import { getCurrentInstance, toRaw } from "vue";
import { mapActions } from "vuex";
import api from "@/helpers/api";
import apiHeader from "@/helpers/apiHeader";
import { useFormConfigEngine } from "@/composables/useFormConfigEngine";
import formConfigHelper from "@/helpers/New_formConfig";

const EMPTY_SET = new Set();

// `split_rows`: baris hasil split -> baris pertama (kepala) grupnya. Murni runtime (tidak ikut payload), dibangun ulang saat
// item dibuka (rebuildSplitGroups). Key = objek baris mentah (toRaw) supaya proxy reaktif & objek asli dianggap sama.
const SPLIT_HEAD = new WeakMap();
// Token per kepala grup: generate yang sudah basi (category diganti lagi sebelum fetch selesai) dibuang.
const SPLIT_TOKEN = new WeakMap();
const splitCodeOrder = (a, b) => String(a ?? "").localeCompare(String(b ?? ""), undefined, { numeric: true });
// `companion_rows`: baris pendamping otomatis -> baris pemicu (sumber) yang diikutinya. Murni runtime, dibangun ulang saat item
// dibuka (syncCompanionRows mengenali baris yang kolom `values`-nya cocok). Key = objek baris mentah (toRaw).
const COMPANION_SOURCE = new WeakMap();

// `rows_source`: baris yang pernah ada per item & field (key baris -> baris), supaya nilai yang sudah diisi kembali kalau
// user kembali ke Item Name / Item Type sebelumnya. Murni runtime, key = objek item mentah (toRaw).
const SOURCE_CACHE = new WeakMap();
// Token per item & field: daftar yang datang terlambat (trigger diganti lagi sebelum fetch selesai) dibuang.
const SOURCE_TOKEN = new WeakMap();
// `row_autofill`: hasil request per URL+param (data mapping statis, mis. ConfigDataMapping) -- satu request untuk semua baris /
// tab dengan param sama. Isinya promise, jadi baris yang meminta bersamaan menunggu request yang sama.
const AUTOFILL_CACHE = new Map();
// Pengawas field level form untuk pemicu baris (lihat watchExternalRowTriggers) per instance tab.
const EXTERNAL_WATCH = new WeakMap();
// Pengawas sumber default field biasa (watchFieldDefaultSources) per instance tab.
const DEFAULT_WATCH = new WeakMap();
const rawStore = (store, item) => {
  const raw = toRaw(item);
  if (!store.has(raw)) store.set(raw, new Map());
  return store.get(raw);
};

// Nilai default_value kolom tabel yang bisa dihitung tanpa fetch (static & relative_date "YYYY-MM-DD"): helper bersama
// resolveSimpleDefault (dipakai juga applyLockedDefaults).
const resolveColumnDefault = (entry) => formConfigHelper.resolveSimpleDefault(entry);

// State dialog standar engine (options/loading/loaded per field, lihat FORM_CONFIG_SCHEMA.md "Kontrak implementasi FE").
// Fungsi, bukan data() di mixin: merge data() mixin Vue 3 dangkal -> `dialog` milik tab akan menimpa utuh.
export const createTabDialog = (item, extra = {}) => ({
  form: item,
  options: {},
  loading: {},
  loaded: {},
  errors: {},
  // Popup Alternative UoM (`alt_uom`): source_field tombol pembuka menentukan kolom yang bisa diedit.
  altUom: { visible: false, openBy: "" },
  ...extra
});

// setup() standar: useFormConfigEngine dengan getter yang sama di semua tab. `extra` = object atau fungsi (vm) => object
// untuk opsi tambahan engine (fieldResolvers, getDetailSources, ...) -- fungsi kalau butuh `vm` (dibaca lazy).
export const useCataloguerTabEngine = (props, extra = {}) => {
  const vm = getCurrentInstance().proxy;
  const options = typeof extra === "function" ? extra(vm) : extra;
  return useFormConfigEngine({
    vm,
    getFormConfig: () => props.formConfig,
    getDialog: () => vm.dialog,
    getMcrType: () => props.mcrType,
    isView: () => props.disabled,
    ...options,
    // Context `mcr_status` (status header MCR, mis. "draft") -- kondisi tampil/wajib ANTAM draft vs non-draft. BE membacanya
    // dari emcr.status (New_CatalougingFormRequest / CataloguingFormService::sanitizeAgainstFormConfig).
    fieldResolvers: { mcr_status: () => props.mcrStatus || "", ...(options.fieldResolvers || {}) }
  });
};

export const cataloguerTabMixin = {
  props: {
    // Item terpilih, sudah di-hydrate New_CataloguerForm. Ditulis langsung (bukan salinan).
    mcrItem: { type: Object, required: true },
    disabled: { type: Boolean, default: false },
    // Config format form engine (form_code FORM02), bukan config lama mcr_process_config
    formConfig: { type: Object, default: null },
    // mcr_type efektif dari New_CataloguerForm (header MCR, atau meta route di halaman search)
    mcrType: { type: String, default: "" },
    // Status header MCR (lowercase, mis. "draft"); kosong di halaman search.
    mcrStatus: { type: String, default: "" },
    // Error validasi Save (New_McrFooter.validateSingleMcrItem) per item -- dipakai hasErrorField field biasa.
    setErrorValidCat: { type: Array, default: () => [] },
    selectedMcrItemIndex: { type: Number, default: 0 }
  },
  watch: {
    // Dibandingkan per OBJEK (bukan id): objek item yang diganti dengan id sama (mis. setAllMcrItem mengarahkan
    // ulang selectedMcrItem) tetap di-bind ulang supaya dialog.form tidak menunjuk objek lama.
    mcrItem(newItem, oldItem) {
      if (!newItem || newItem === oldItem) return;
      this.syncFromItem();
    }
  },
  mounted() {
    this.syncFromItem();
  },
  methods: {
    ...mapActions("mcr", ["deleteCertainErrorValidation"]),
    // Bind dialog.form ke item & buang state opsi item sebelumnya (opsi bisa beda per item: depends_on, per baris).
    // Logic khusus tab (preload label, inisialisasi tabel, dst) ditaruh di hook onItemBound() tab masing-masing.
    syncFromItem() {
      this.dialog.form = this.mcrItem;
      this.dialog.options = {};
      this.dialog.loading = {};
      this.dialog.loaded = {};
      this.applyLockedValues();
      if (typeof this.onItemBound === "function") this.onItemBound();
    },
    // ===== `readonlyandsaved` (FORM_CONFIG_SCHEMA.md): tampil terkunci, nilai tetap disimpan =====
    isFieldLocked(field, tab) {
      return formConfigHelper.isReadonlySaved(this.formConfig, tab, field, this.formConfigCtx);
    },
    isColumnLocked(tab, field, column, row) {
      return formConfigHelper.isColumnReadonlySaved(this.formConfig, tab, field, column, this.buildRowCtx(row));
    },
    // Nilai field/kolom terkunci ber-default_value dihitung ulang ke objek item (dipakai semua tab & payload Save). Mode
    // lihat-saja tidak mengubah data.
    applyLockedValues() {
      if (this.disabled) return;
      formConfigHelper.applyLockedDefaults(this.formConfig, this.mcrItem, this.formConfigCtx);
    },
    // ===== Opsi kolom field repeatable (tabel) -- generic per config, dipakai semua tab yang punya tabel =====
    // Kunci cache opsi 1 kolom = PARAM request untuk baris itu (dihitung dengan ctx per baris yang SAMA dengan
    // engine), BUKAN nomor baris: baris dengan param sama (mis. plant sama) berbagi 1 daftar & 1 request; param
    // berubah (plant diganti) -> kunci baru -> fetch sekali. Kolom yang param-nya tidak bergantung baris (mis. daftar
    // plant, cuma `client`) otomatis 1 kunci bersama. detail_array: kunci dari nilai `filter`-nya (dihitung lokal).
    columnOptionsKey(tab, field, column, row) {
      const dataSource = formConfigHelper.getColumnConfig(this.formConfig, tab, field, column)?.data_source;
      const base = `${field}.${column}`;
      if (!dataSource || dataSource.type === "array") return base;
      const ctx = this.buildRowCtx(row);
      const values = dataSource.type === "detail_array"
        ? Object.fromEntries(Object.values(dataSource.filter || {}).map((name) => [name, ctx.getField(name)]))
        : formConfigHelper.resolveDataSourceParams(dataSource, ctx);
      const query = Object.keys(values).sort().map((key) => `${key}=${values[key] ?? ""}`).join("&");
      return query ? `${base}?${query}` : base;
    },
    // Kolom dataset besar (search-as-you-type) = keputusan template per tab, bukan JSON (DECISIONS_LOG.md "Field
    // dataset besar..."). Tab mendaftarkannya lewat data() `searchAsYouTypeColumns: new Set(["<field>.<kolom>"])`.
    isSearchColumn(field, column) {
      return (this.searchAsYouTypeColumns || EMPTY_SET).has(`${field}.${column}`);
    },
    // Handler @show kolom tabel. Opsi API di-cache per kunci (columnOptionsKey): buka ulang / baris lain dengan param
    // sama tidak memanggil API lagi. detail_array selalu dihitung ulang (turunan state form yang bisa berubah, mis.
    // plant yang dipilih di tabel lain) -- lokal, tanpa API. Kolom search-as-you-type: muat halaman pertama (search
    // kosong) dan DIGABUNG (merge) ke opsi yang ada, supaya label baris lain yang sudah di-resolve tidak hilang.
    async loadColumnOptions(tab, field, column, row) {
      const dataSource = formConfigHelper.getColumnConfig(this.formConfig, tab, field, column)?.data_source;
      const search = this.isSearchColumn(field, column);
      await this.ensureParamFromOptions(dataSource);
      return this.loadSelectFieldOptions(field, {
        tab,
        column,
        row,
        optionsKey: this.columnOptionsKey(tab, field, column, row),
        force: dataSource?.type === "detail_array",
        ...(search ? { search: "", merge: true } : {})
      });
    },
    // Param `{ from, attribute }` membaca atribut OPSI field `from` yang sedang dipilih (mis. account_category_ref Material
    // Type untuk filter Valuation Class). Field `from` bisa milik tab lain (Material Type ada di Basic Data) -- opsinya
    // dimuat dulu ke state tab ini dengan config tab asalnya, supaya param-nya terisi sebelum opsi kolom diminta.
    async ensureParamFromOptions(dataSource) {
      for (const entry of dataSource?.params || []) {
        if (!entry || typeof entry !== "object" || !entry.from) continue;
        const value = this.mcrItem?.[entry.from];
        if (!formConfigHelper.isFilledValue(value)) continue;
        if ((this.dialog.options[entry.from] || []).some((o) => String(o.value) === String(value))) continue;
        const fromTab = this.findFieldTab(entry.from);
        // eslint-disable-next-line no-await-in-loop
        if (fromTab) await this.loadSelectFieldOptions(entry.from, { tab: fromTab, force: true, merge: true });
      }
    },
    // Handler @filter kolom search-as-you-type: cari ke server (debounce per kunci opsi), hasil digabung (merge) --
    // SelectPrime `filter` tetap menyaring daftar gabungan itu sesuai ketikan.
    searchColumnOptions(event, tab, field, column, row) {
      this.searchSelectFieldOptions(event, field, {
        tab,
        column,
        row,
        optionsKey: this.columnOptionsKey(tab, field, column, row),
        merge: true
      });
    },
    // Label "code - description" baris yang sudah berisi value (item dibuka), tanpa menunggu dropdown dibuka.
    // Kolom biasa: satu request per kunci unik (kunci yang sudah dimuat / sedang dimuat dilewati engine).
    // Kolom search-as-you-type: tidak memuat seluruh daftar -- cari per value unik, BERURUTAN + merge (pola
    // preloadRepeatableFieldLabels engine) supaya respons yang datang bersamaan tidak saling menimpa.
    async preloadColumnLabels(tab, field) {
      const columns = formConfigHelper.getFieldConfig(this.formConfig, tab, field)?.columns || {};
      const rows = this.mcrItem?.[field] || [];
      for (const [column, colConfig] of Object.entries(columns)) {
        const dataSource = colConfig?.data_source;
        if (!dataSource || dataSource.type === "detail_array") continue;
        const filledRows = rows.filter((row) => formConfigHelper.isFilledValue(row[column]));
        if (!this.isSearchColumn(field, column)) {
          filledRows.forEach((row) => this.loadColumnOptions(tab, field, column, row));
          continue;
        }
        // Berurutan per kunci opsi (merge ke daftar yang sama), kunci berbeda paralel (mis. nilai Characteristic: satu
        // kunci per cin_code -- tidak saling menimpa).
        const groups = new Map();
        filledRows.forEach((row) => {
          const optionsKey = this.columnOptionsKey(tab, field, column, row);
          if (!groups.has(optionsKey)) groups.set(optionsKey, []);
          groups.get(optionsKey).push(row);
        });
        // eslint-disable-next-line no-await-in-loop
        await Promise.all([...groups].map(async ([optionsKey, groupRows]) => {
          for (const row of groupRows) {
            const known = (this.dialog.options[optionsKey] || []).some((o) => String(o.value) === String(row[column]));
            if (known) continue;
            // eslint-disable-next-line no-await-in-loop
            await this.loadSelectFieldOptions(field, { tab, column, row, optionsKey, force: true, search: String(row[column]), merge: true });
          }
        }));
      }
    },
    // Label semua field select di satu tab saat item dibuka: field biasa yang visible lewat resolveSelectedFieldLabel,
    // field tabel lewat preloadColumnLabels. Generic per config -- dipanggil dari onItemBound() tab.
    preloadTabLabels(tab) {
      Object.entries(formConfigHelper.getTabFieldMap(this.formConfig, tab) || {}).forEach(([field, fc]) => {
        if (fc?.columns) {
          this.preloadColumnLabels(tab, field);
          return;
        }
        if (this.isFieldVisibleFn(field, tab)) this.resolveSelectedFieldLabel(field, tab, this.mcrItem[field]);
      });
    },
    // Field tabel milik tab yang belum berbentuk array (item baru / response tanpa relasi) -> []. Field `rows_from`
    // dilewati: array-nya milik field sumber (di-link New_CataloguerForm.hydrateItem), jangan diganti array baru.
    ensureTableArrays(tab) {
      Object.entries(formConfigHelper.getTabFieldMap(this.formConfig, tab) || {}).forEach(([field, fc]) => {
        if (fc?.columns && !fc.rows_from && !Array.isArray(this.mcrItem[field])) this.mcrItem[field] = [];
      });
    },
    // ===== Handler generik field & tabel (semua parameter eksplisit, jadi aman dipakai lintas tab & baris) =====
    // @change field select biasa: hapus error Save field itu & reset field yang depends_on ke field ini (dari config).
    onFieldChange(field, tab) {
      this.delCertErrorVal(field);
      this.onFieldDependencyChange(field, tab);
      this.applyLockedValues();
      this.syncAltUomFromField(field, tab);
      // Field yang depends_on ke field ini (sesuai mcr_type saat ini) & ber-`auto_select_single`: kalau opsinya cuma satu,
      // langsung dipilih (mis. Item Name dipilih -> Material Type/Group INALUM MCR001).
      Object.keys(formConfigHelper.getTabFieldMap(this.formConfig, tab) || {}).forEach((dependent) => {
        if (formConfigHelper.getFieldDependsOn(this.formConfig, tab, dependent, this.formConfigCtx).includes(field)) {
          this.autoSelectSingleOption(dependent, tab);
        }
      });
      // Tabel ber-`rows_source` yang trigger-nya field ini dibuat ulang (mis. Item Name / Item Type -> Characteristic).
      this.sourcedTableFields(field).forEach((sourced) => this.refreshSourcedRows(sourced.tab, sourced.field));
    },
    // Saat item dibuka: field ber-`auto_select_single` yang masih kosong dicoba dipilih otomatis (opsi tunggal).
    // Mode lihat-saja (halaman search, material-request) tidak pernah mengubah data.
    autoSelectTabFields(tab) {
      if (this.disabled) return;
      Object.keys(formConfigHelper.getTabFieldMap(this.formConfig, tab) || {}).forEach((field) => {
        if (this.isFieldVisibleFn(field, tab) && this.isFieldEnabledFn(field, tab)) this.autoSelectSingleOption(field, tab);
      });
    },
    // Tabel (card-nya) tampil kalau ADA kolom yang visible untuk mcr_type/company saat ini.
    isTableVisible(tab, field) {
      return formConfigHelper.hasVisibleColumn(this.formConfig, tab, field, this.formConfigCtx);
    },
    // Tabel wajib punya baris kalau row_rules min_rows > 0 (atau ada row_rules "required") -- hitungan yang sama
    // dengan validasi Save (getRequiredRowCount).
    isTableMandatory(tab, field) {
      return formConfigHelper.getRequiredRowCount(this.formConfig, tab, field, this.formConfigCtx) > 0;
    },
    // Baris baru: semua kolom dari config bernilai kosong, lalu default_value kolom (static / relative_date,
    // first-match-wins, `when` dievaluasi dengan ctx baris baru) -- mis. valid_until 9999-12-31 di Manufacture.
    // Baris field ini juga baris field yang `rows_from`-nya field ini (mis. Plant -> General Plant Data), jadi default
    // kolom field-field itu ikut diisi -- cuma kolom yang punya default & visible, kolom yang sudah ada tidak ditimpa.
    addRow(tab, field) {
      // Tabel `split_rows.auto` (Sales INALUM): satu baris = satu grup utuh (satu baris per opsi kolom target, mis. Distribution
      // Channel 10 & 20) -- Add membuat seluruh grup sekaligus.
      if (this.splitConfig(tab, field)?.auto) {
        this.ensureAutoSplitRows(tab, { force: true, onlyField: field });
        return;
      }
      const row = this.buildNewRow(tab, field);
      const ctx = this.buildRowCtx(row);
      this.linkedTableFields(tab, field).slice(1).forEach((linked) => {
        const linkedColumns = formConfigHelper.getFieldConfig(this.formConfig, linked.tab, linked.field)?.columns || {};
        Object.keys(linkedColumns).forEach((column) => {
          if (column in row || !formConfigHelper.isColumnVisible(this.formConfig, linked.tab, linked.field, column, ctx)) return;
          const value = resolveColumnDefault(formConfigHelper.resolveActiveColumnDefault(this.formConfig, linked.tab, linked.field, column, ctx));
          if (value !== undefined) row[column] = value;
        });
      });
      if (!Array.isArray(this.mcrItem[field])) this.mcrItem[field] = [];
      this.mcrItem[field].push(row);
      this.applyLockedValues();
    },
    // Baris baru satu field tabel: semua kolom config kosong + default_value kolom (static / relative_date).
    buildNewRow(tab, field) {
      const columns = formConfigHelper.getFieldConfig(this.formConfig, tab, field)?.columns || {};
      const row = Object.fromEntries(Object.keys(columns).map((column) => [column, ""]));
      const ctx = this.buildRowCtx(row);
      Object.keys(columns).forEach((column) => {
        const value = resolveColumnDefault(formConfigHelper.resolveActiveColumnDefault(this.formConfig, tab, field, column, ctx));
        if (value !== undefined) row[column] = value;
      });
      return row;
    },
    // Kunci opsi tidak bergantung nomor baris (columnOptionsKey), jadi hapus baris tidak perlu membangun ulang opsi.
    // Baris tabel lain yang di-`sync_rows` dari baris ini ikut dihapus (mis. Warehouse -> Storage Type).
    removeRow(field, index) {
      const [removed] = this.mcrItem[field].splice(index, 1);
      // Kepala grup `split_rows` dihapus -> baris hasil split-nya ikut dihapus.
      if (removed) this.removeSplitFollowers(field, removed);
      // Pemicu `companion_rows` dihapus -> pendampingnya ikut terhapus / pindah ke pemicu lain kombinasi yang sama.
      Object.keys(this.formConfig?.tab_fields || {}).forEach((tab) => {
        if (this.companionConfig(tab, field)) this.syncCompanionRows(tab, field);
      });
      this.syncLinkedRows(field);
      this.syncAltUomFromColumns(field);
      this.applyLockedValues();
      this.applyWatchedRowAutofill(field);
    },
    // ===== `alt_uom` (FORM_CONFIG_SCHEMA.md): field / kolom tabel -> baris Alternative UoM item =====
    // `alt_uom: { field, source_field, base? }` di config field biasa (UoM) atau KOLOM tabel (Sales Unit): nilainya jadi satu
    // baris di tabel `field` (kolom source_field = penanda). `base` = Base UoM: baris pertama, Numerator & Denumerator "1".
    // Baris lain: Numerator diisi user di popup, Denumerator ikut baris pertama; nilai kosong -> baris dihapus. Kolom tabel
    // yang barisnya >1 (Sales per distribution channel): nilai TERAKHIR yang terisi yang dipakai.
    altUomOf(tab, field, column) {
      const fc = formConfigHelper.getFieldConfig(this.formConfig, tab, field);
      return (column ? fc?.columns?.[column] : fc)?.alt_uom || null;
    },
    // Sinkron satu entri `alt_uom` dengan nilai sumbernya. onlyCreate: cuma buat baris yang belum ada (item dibuka).
    applyAltUom(alt, value, { onlyCreate = false } = {}) {
      if (this.disabled || !alt?.field) return;
      if (!Array.isArray(this.mcrItem[alt.field])) this.mcrItem[alt.field] = [];
      const rows = this.mcrItem[alt.field];
      const code = formConfigHelper.isFilledValue(value) ? String(value).trim() : "";
      const index = rows.findIndex((row) => row.source_field === alt.source_field);
      if (onlyCreate && index >= 0) return;
      if (!code) {
        // Base UoM dikosongkan -> semua baris Alternative UoM tidak punya acuan lagi.
        if (alt.base) rows.splice(0, rows.length);
        else if (index >= 0) rows.splice(index, 1);
        return;
      }
      if (index >= 0) {
        rows[index].alternative_unit_of_measure = code;
        if (alt.base) {
          if (!formConfigHelper.isFilledValue(rows[index].numerator)) rows[index].numerator = "1";
          if (!formConfigHelper.isFilledValue(rows[index].denumerator)) rows[index].denumerator = "1";
        }
        return;
      }
      if (alt.base) {
        rows.unshift({ alternative_unit_of_measure: code, numerator: "1", denumerator: "1", source_field: alt.source_field });
      } else if (rows.length) {
        // Tanpa Base UoM baris ini tidak punya acuan -> tidak dibuat (tombol popup memberi tahu user).
        rows.push({ alternative_unit_of_measure: code, numerator: "", denumerator: rows[0].denumerator || "", source_field: alt.source_field });
      }
    },
    syncAltUomFromField(field, tab) {
      const alt = this.altUomOf(tab, field);
      if (alt) this.applyAltUom(alt, this.mcrItem[field]);
    },
    // Semua kolom tabel `field` ber-`alt_uom` (atau cuma `column`): nilai terakhir yang terisi dipakai.
    syncAltUomFromColumns(field, column) {
      const tab = this.findFieldTab(field);
      if (!tab) return;
      const columns = formConfigHelper.getFieldConfig(this.formConfig, tab, field)?.columns || {};
      Object.entries(columns).forEach(([name, colConfig]) => {
        if (!colConfig?.alt_uom || (column && column !== name)) return;
        const filled = (this.mcrItem[field] || []).map((row) => row?.[name]).filter((v) => formConfigHelper.isFilledValue(v));
        this.applyAltUom(colConfig.alt_uom, filled.length ? filled[filled.length - 1] : "");
      });
    },
    // Saat item dibuka: baris Base UoM yang belum ada dibuatkan dari UoM item (data lama). Baris lain tidak disentuh.
    syncAllAltUom() {
      if (this.disabled) return;
      Object.keys(this.formConfig?.tab_fields || {}).forEach((tab) => {
        Object.entries(formConfigHelper.getTabFieldMap(this.formConfig, tab) || {}).forEach(([field, fc]) => {
          if (fc?.alt_uom?.base) this.applyAltUom(fc.alt_uom, this.mcrItem[field], { onlyCreate: true });
        });
      });
    },
    // Tombol popup: aktif kalau sumbernya terisi; mode lihat-saja kalau sudah ada baris yang bisa dilihat.
    // Tabel Alternative UoM (isi popup) masih punya error Save aktif -> tombol popup ditandai merah. Popup menampilkan SEMUA baris,
    // jadi semua tombol Alternative UoM (UoM di Basic Data, Sales Unit di Sales) ikut merah.
    hasAltUomError(alt) {
      return (this.mcrItem?.[alt?.field] || []).some((row) => formConfigHelper.rowHasActiveError(row));
    },
    canOpenAltUom(alt, value) {
      if (this.disabled) return (this.mcrItem[alt?.field] || []).length > 0;
      return formConfigHelper.isFilledValue(value);
    },
    openAltUomPopup(alt, value) {
      if (!alt || !this.canOpenAltUom(alt, value)) return;
      if (!this.disabled) {
        this.applyAltUom(alt, value);
        if (!(this.mcrItem[alt.field] || []).some((row) => row.source_field === alt.source_field)) {
          this.$toast?.add({ severity: "warn", summary: "Validation", detail: "Fill the Unit of Measurement first.", life: 3000 });
          return;
        }
      }
      this.dialog.altUom = { visible: true, openBy: alt.source_field };
    },
    // ===== `split_rows` (FORM_CONFIG_SCHEMA.md): satu baris dipecah jadi satu baris per opsi kolom target =====
    // Mis. Accounting INALUM/TIMAH: Valuation Category dipilih di baris pertama -> satu baris per Valuation Type category itu
    // (code kosong dulu, lalu C1, C2, ...); kolom `follow` di baris hasil split selalu ikut baris pertama.
    splitConfig(tab, field) {
      const columns = formConfigHelper.getFieldConfig(this.formConfig, tab, field)?.columns || {};
      const entry = Object.entries(columns).find(([, col]) => col?.split_rows?.column);
      return entry ? { trigger: entry[0], ...entry[1].split_rows } : null;
    },
    isSplitFollower(row) {
      return !!row && SPLIT_HEAD.has(toRaw(row));
    },
    // Kolom target split (Valuation Type) diisi otomatis -> tidak diedit manual di tabel yang split-nya aktif.
    isSplitTargetColumn(tab, field, column) {
      return this.splitConfig(tab, field)?.column === column;
    },
    removeSplitFollowers(field, head) {
      const rows = this.mcrItem[field] || [];
      const rawHead = toRaw(head);
      for (let i = rows.length - 1; i >= 0; i -= 1) {
        if (SPLIT_HEAD.get(toRaw(rows[i])) === rawHead) {
          SPLIT_HEAD.delete(toRaw(rows[i]));
          rows.splice(i, 1);
        }
      }
    },
    // Grup dikenali ulang saat item dibuka: baris dengan kolom pemicu (category) & kolom `follow` yang sama = satu grup,
    // baris pertama jadi kepala. Data tersimpan tidak membawa penanda grup.
    rebuildSplitGroups(tab) {
      Object.keys(formConfigHelper.getTabFieldMap(this.formConfig, tab) || {}).forEach((field) => {
        const split = this.splitConfig(tab, field);
        if (!split) return;
        const heads = new Map();
        (this.mcrItem[field] || []).forEach((row, index, rows) => {
          SPLIT_HEAD.delete(toRaw(row));
          // `auto`: tanpa pemicu -- baris pertama kepala, semua baris sesudahnya hasil split.
          if (split.auto) {
            if (index > 0) SPLIT_HEAD.set(toRaw(row), toRaw(rows[0]));
            return;
          }
          if (!formConfigHelper.isFilledValue(row[split.trigger])) return;
          const key = JSON.stringify([split.trigger, ...(split.follow || [])].map((name) => row[name] ?? ""));
          if (heads.has(key)) SPLIT_HEAD.set(toRaw(row), toRaw(heads.get(key)));
          else heads.set(key, row);
        });
      });
    },
    // Dipanggil onColumnChange: kolom pemicu di kepala grup diubah -> baris hasil split lama dihapus lalu dibuat ulang dari
    // opsi kolom target (difilter nilai pemicu); kolom `follow` di kepala diubah -> disalin ke baris hasil split-nya.
    async applySplitRows(tab, field, column, row) {
      const split = this.splitConfig(tab, field);
      if (!split || this.isSplitFollower(row)) return;
      const rows = this.mcrItem[field] || [];
      const rawHead = toRaw(row);
      if ((split.follow || []).includes(column)) {
        rows.filter((other) => SPLIT_HEAD.get(toRaw(other)) === rawHead).forEach((other) => { other[column] = row[column]; });
        return;
      }
      if (split.auto || column !== split.trigger) return;
      const token = {};
      SPLIT_TOKEN.set(rawHead, token);
      this.removeSplitFollowers(field, row);
      // Kolom target dikosongkan jadi null ("belum ada"), bukan "": "" = kode kosong yang SENGAJA (mis. Valuation Type kepala
      // split INALUM dari master), dipertahankan BE (ConvertEmptyStringsToNullExcept). Kalau opsinya ada, langsung diisi di bawah.
      row[split.column] = null;
      if (!formConfigHelper.isFilledValue(row[split.trigger])) return;
      await this.loadColumnOptions(tab, field, split.column, row);
      if (SPLIT_TOKEN.get(rawHead) !== token) return;
      const codes = (this.dialog.options[this.columnOptionsKey(tab, field, split.column, row)] || [])
        .map((option) => option.value ?? "").sort(splitCodeOrder);
      if (!codes.length) return;
      row[split.column] = codes[0];
      const generated = codes.slice(1).map((code) => {
        const newRow = this.buildNewRow(tab, field);
        [split.trigger, ...(split.follow || [])].forEach((name) => { newRow[name] = row[name]; });
        newRow[split.column] = code;
        SPLIT_HEAD.set(toRaw(newRow), rawHead);
        return newRow;
      });
      rows.splice(rows.indexOf(row) + 1, 0, ...generated);
      this.applyLockedValues();
    },
    // ===== `companion_rows` (FORM_CONFIG_SCHEMA.md): baris pendamping otomatis per kombinasi kolom `key` =====
    // Mis. Accounting ANTAM: Valuation Type C1-NEW/C2-REFUR/C3-BROKEN/C4-EXPROJ dipilih -> satu baris Valuation Type "" per
    // Valuation Area + Category, kolom `follow` ikut baris pemicu PERTAMA kombinasi itu. Baris pendamping terkunci & tidak bisa
    // dihapus; tidak ada pemicu lagi untuk kombinasinya (pemicu dihapus / Valuation Type dikosongkan) -> ikut terhapus.
    companionConfig(tab, field) {
      return formConfigHelper.getFieldConfig(this.formConfig, tab, field)?.companion_rows || null;
    },
    isCompanionRow(row) {
      return !!row && COMPANION_SOURCE.has(toRaw(row));
    },
    syncAllCompanionRows(tab) {
      Object.keys(formConfigHelper.getTabFieldMap(this.formConfig, tab) || {}).forEach((field) => this.syncCompanionRows(tab, field));
    },
    syncCompanionRows(tab, field) {
      const config = this.companionConfig(tab, field);
      const rows = this.mcrItem?.[field];
      if (!config || this.disabled || !Array.isArray(rows) || !this.isTableVisible(tab, field)) return;
      const keyColumns = [].concat(config.key || []);
      const values = config.values || {};
      const keyOf = (row) => JSON.stringify(keyColumns.map((column) => row?.[column] ?? ""));
      const matchesValues = (row) => Object.entries(values).every(([column, value]) => row?.[column] === value);
      // Kombinasi `key` -> baris pemicu pertama (urutan tabel).
      const groups = new Map();
      rows.forEach((row) => {
        if (this.isCompanionRow(row) || !formConfigHelper.evaluateCondition(config.when, this.buildRowCtx(row))) return;
        const key = keyOf(row);
        if (!groups.has(key)) groups.set(key, row);
      });
      const used = new Set();
      const free = (row) => !used.has(toRaw(row));
      groups.forEach((source, key) => {
        const rawSource = toRaw(source);
        // Pakai ulang: pendamping milik pemicu ini -> pendamping lain dengan kombinasi sama -> baris tersimpan yang kolom
        // `values`-nya cocok (item dibuka lagi) -> baru dibuat tepat di bawah baris pemicu.
        let companion = rows.find((row) => free(row) && COMPANION_SOURCE.get(toRaw(row)) === rawSource)
          || rows.find((row) => free(row) && this.isCompanionRow(row) && keyOf(row) === key)
          || rows.find((row) => free(row) && !this.isCompanionRow(row) && matchesValues(row) && keyOf(row) === key
            && !formConfigHelper.evaluateCondition(config.when, this.buildRowCtx(row)));
        if (!companion) {
          companion = this.buildNewRow(tab, field);
          rows.splice(rows.indexOf(source) + 1, 0, companion);
        }
        used.add(toRaw(companion));
        [].concat(config.follow || []).forEach((column) => { companion[column] = source[column]; });
        Object.entries(values).forEach(([column, value]) => { companion[column] = value; });
        COMPANION_SOURCE.set(toRaw(companion), rawSource);
      });
      for (let i = rows.length - 1; i >= 0; i -= 1) {
        if (this.isCompanionRow(rows[i]) && free(rows[i])) {
          COMPANION_SOURCE.delete(toRaw(rows[i]));
          rows.splice(i, 1);
        }
      }
    },
    // `split_rows.auto` (mis. Sales INALUM: satu baris per Distribution Channel): tanpa kolom pemicu. Tabel yang tampil dijamin
    // punya satu baris per opsi kolom target -- baris pertama kepala (diedit user), sisanya hasil split (terkunci, `follow`
    // ikut kepala). Baris yang sudah ada dipertahankan, cuma kode yang belum ada ditambah. Mode lihat-saja / tabel tidak
    // tampil dilewati. Baris cuma dibuat / dilengkapi kalau `split_rows.auto_create` cocok (boolean | condition, default true)
    // atau `force` (tombol Add) -- mis. Sales INALUM MCR001 ZFGD otomatis, ZSFG/ZNOV lewat Add, MCR002 tidak pernah (baris dari
    // data material apa adanya). Penanda grup baris yang sudah ada tetap dari rebuildSplitGroups.
    async ensureAutoSplitRows(tab, { force = false, onlyField = null } = {}) {
      if (this.disabled) return;
      const fields = onlyField ? [onlyField] : Object.keys(formConfigHelper.getTabFieldMap(this.formConfig, tab) || {});
      for (const field of fields) {
        const split = this.splitConfig(tab, field);
        if (!split?.auto || !this.isTableVisible(tab, field)) continue;
        if (!Array.isArray(this.mcrItem[field])) this.mcrItem[field] = [];
        const rows = this.mcrItem[field];
        if (!force && !this.isAutoSplitCreateActive(tab, field)) continue;
        const item = this.mcrItem;
        const probe = rows[0] || this.buildNewRow(tab, field);
        await this.loadColumnOptions(tab, field, split.column, probe);
        // Item diganti / tabel disembunyikan selama menunggu opsi.
        if (item !== this.mcrItem || this.mcrItem[field] !== rows || !this.isTableVisible(tab, field)) continue;
        const codes = (this.dialog.options[this.columnOptionsKey(tab, field, split.column, probe)] || [])
          .map((option) => option.value ?? "").filter((code) => code !== "").sort(splitCodeOrder);
        if (!codes.length) continue;
        if (!rows.length) rows.push(probe);
        const head = rows[0];
        if (!formConfigHelper.isFilledValue(head[split.column])) {
          head[split.column] = codes.find((code) => !rows.some((row) => String(row[split.column]) === String(code))) ?? codes[0];
        }
        codes.forEach((code) => {
          if (rows.some((row) => String(row[split.column]) === String(code))) return;
          const newRow = this.buildNewRow(tab, field);
          (split.follow || []).forEach((name) => { newRow[name] = head[name]; });
          newRow[split.column] = code;
          rows.push(newRow);
        });
        rows.forEach((row, index) => {
          if (index > 0) SPLIT_HEAD.set(toRaw(row), toRaw(head));
          else SPLIT_HEAD.delete(toRaw(row));
        });
        this.applyLockedValues();
      }
    },
    // `split_rows.auto_create` tabel `auto`: tabel kosong dibuatkan baris otomatis? Tanpa key = true (perilaku lama).
    isAutoSplitCreateActive(tab, field) {
      const split = this.splitConfig(tab, field);
      if (!split?.auto) return false;
      const rule = split.auto_create;
      if (rule === undefined || rule === null) return true;
      return typeof rule === "boolean" ? rule : formConfigHelper.evaluateCondition(rule, this.formConfigCtx);
    },
    // Tab (key config) tempat field berada -- untuk field tabel yang dirujuk config dari tab lain.
    findFieldTab(field) {
      return Object.keys(this.formConfig?.tab_fields || {}).find((tab) => !!formConfigHelper.getFieldConfig(this.formConfig, tab, field));
    },
    // `sync_rows` kolom (FORM_CONFIG_SCHEMA.md): tiap nilai kolom ini di baris field ini punya baris di field target (kolom
    // `key` = nilai itu). Nilai baru tanpa baris target -> baris target dibuat (default kolom + `copy` dari baris sumber);
    // baris target yang `key`-nya tidak lagi dipakai baris sumber mana pun -> dihapus (nilai diganti / baris sumber
    // dihapus). Kolom `copy` yang diubah user di baris sumber ikut diperbarui di baris target yang sama key-nya.
    // Berantai: tabel target yang berubah ikut menyinkronkan tabel target-nya (mis. Plant -> Warehouse -> Storage Type).
    syncLinkedRows(field, changedColumn, changedRow, visited = new Set()) {
      if (visited.has(field)) return;
      visited.add(field);
      const tab = this.findFieldTab(field);
      const columns = formConfigHelper.getFieldConfig(this.formConfig, tab, field)?.columns || {};
      const rows = this.mcrItem[field] || [];
      Object.entries(columns).forEach(([column, colConfig]) => {
        const sync = colConfig?.sync_rows;
        const targetTab = sync?.field && sync.key ? this.findFieldTab(sync.field) : null;
        if (!targetTab) return;
        if (!Array.isArray(this.mcrItem[sync.field])) this.mcrItem[sync.field] = [];
        const targetRows = this.mcrItem[sync.field];
        const copy = sync.copy || [];
        const used = new Set(rows.map((row) => row[column]).filter((v) => formConfigHelper.isFilledValue(v)).map(String));
        for (let i = targetRows.length - 1; i >= 0; i -= 1) {
          const key = targetRows[i][sync.key];
          if (formConfigHelper.isFilledValue(key) && !used.has(String(key))) targetRows.splice(i, 1);
        }
        rows.forEach((row) => {
          const value = row[column];
          if (!formConfigHelper.isFilledValue(value)) return;
          const matches = targetRows.filter((target) => String(target[sync.key]) === String(value));
          if (!matches.length) {
            const newRow = this.buildNewRow(targetTab, sync.field);
            newRow[sync.key] = value;
            copy.forEach((name) => { newRow[name] = row[name]; });
            targetRows.push(newRow);
            // Baris target baru: `row_autofill`-nya jalan (mis. Warehouse INALUM dari WAREHOUSE_CONFIG per plant). Lewat
            // proxy reaktif (targetRows), bukan objek mentah, supaya hasil autofill tampil.
            this.runRowAutofillEntries(targetTab, sync.field, targetRows[targetRows.length - 1]);
          } else if (row === changedRow && copy.includes(changedColumn)) {
            matches.forEach((target) => { target[changedColumn] = row[changedColumn]; });
          }
        });
        this.syncLinkedRows(sync.field, undefined, undefined, visited);
      });
    },
    // Semua `sync_rows` di config dijalankan sekali -- dipanggil tab saat item dibuka (bukan mode lihat-saja), supaya data
    // lama yang belum sinkron (mis. plant tanpa baris Warehouse) dirapikan sebelum user mengedit.
    syncAllLinkedRows() {
      const visited = new Set();
      Object.keys(this.formConfig?.tab_fields || {}).forEach((tab) => {
        Object.entries(formConfigHelper.getTabFieldMap(this.formConfig, tab) || {}).forEach(([field, fc]) => {
          if (Object.values(fc?.columns || {}).some((col) => col?.sync_rows)) this.syncLinkedRows(field, undefined, undefined, visited);
        });
      });
    },
    // Opsi select kolom tabel. Kolom yang diatur unik (`row_checks` type "unique"): nilai yang sudah dipakai baris LAIN
    // tidak ditawarkan -- untuk aturan gabungan, cuma baris lain yang kolom pasangannya sama (mis. Storage Type ANTAM unik
    // per plant + warehouse_number). Label tetap dari daftar lengkap (getSelectValueLabelFn).
    columnSelectOptions(tab, field, column, row) {
      const options = this.dialog.options[this.columnOptionsKey(tab, field, column, row)];
      const peerGroups = formConfigHelper.getUniqueColumnPeers(this.formConfig, tab, field, column);
      if (!options || !peerGroups.length) return options;
      const same = (a, b) => String(a ?? "") === String(b ?? "");
      const used = new Set((this.mcrItem[field] || [])
        .filter((other) => other !== row && formConfigHelper.isFilledValue(other[column]))
        .filter((other) => peerGroups.some((peers) => peers.every((peer) => same(other[peer], row[peer]))))
        .map((other) => String(other[column])));
      return options.filter((option) => !used.has(String(option.value)));
    },
    // Tanda * di header kolom tabel yang aturan wajibnya bergantung pada BARIS (mis. MRP: Sparepart Category wajib cuma
    // plant A001-A004, Service Level wajib kalau MRP Controller baris itu MRS/SPP): tampil kalau minimal satu baris
    // mewajibkannya (ctx per baris, sama dengan validasi Save & BE). Tabel tanpa baris -> ctx form seperti isColumnMandatoryFn.
    isColumnMandatoryInRows(tab, field, column) {
      const rows = this.mcrItem?.[field];
      if (!Array.isArray(rows) || !rows.length) return this.isColumnMandatoryFn(column, field, tab);
      return rows.some((row) => formConfigHelper.isColumnMandatory(this.formConfig, tab, field, column, this.buildRowCtx(row)));
    },
    // Panjang maksimum kolom teks dari rules `max_length` config (atribut maxlength InputText); tidak ada rule -> null.
    // Awalan `value_prefix` yang SUDAH menempel di nilai baris tidak dihitung (mis. "(S)" Type Z2: maks 40 di luar awalan) --
    // sama dengan BE FormConfigRuleBuilder (max ditambah panjang awalan yang berlaku).
    columnMaxLength(tab, field, column, row) {
      const colConfig = formConfigHelper.getColumnConfig(this.formConfig, tab, field, column);
      const rule = (colConfig?.rules || []).find((r) => r?.type === "max_length");
      if (!rule?.value) return null;
      const prefix = colConfig?.value_prefix?.value;
      const value = row?.[column];
      const hasPrefix = typeof prefix === "string" && prefix !== "" && typeof value === "string" && value.startsWith(prefix);
      return Number(rule.value) + (hasPrefix ? prefix.length : 0);
    },
    // Tombol Add / hapus baris dari `row_actions` config (default boleh, mis. Manufacture cuma MCR001/MCR003).
    // Mode lihat-saja tetap lewat prop `disabled`, bukan dari sini.
    canAddRow(tab, field) {
      // Tabel `split_rows.auto` cuma punya satu grup (satu baris per opsi kolom target): Add cuma selama tabel masih kosong.
      if (this.splitConfig(tab, field)?.auto && (this.mcrItem?.[field] || []).length) return false;
      return formConfigHelper.isRowActionAllowed(this.formConfig, tab, field, "add", this.formConfigCtx);
    },
    canRemoveRow(tab, field) {
      return formConfigHelper.isRowActionAllowed(this.formConfig, tab, field, "remove", this.formConfigCtx);
    },
    // Handler @change kolom tabel (generic per config): di BARIS yang sama, kolom yang `depends_on` kolom ini dikosongkan
    // dan kolom ber-default_value `derived` dari kolom ini diisi ulang -- berantai (mis. plant -> mrp_controller kosong
    // -> service_level ikut kosong). Berlaku juga untuk field yang `rows_from`-nya field ini (barisnya objek yang SAMA,
    // mis. ganti plant di tabel Plant ikut mengosongkan MRP Controller di tab MRP). Opsi tidak perlu di-reset: kuncinya
    // dari param request (columnOptionsKey), plant baru = kunci baru.
    onColumnChange(tab, field, column, row) {
      this.linkedTableFields(tab, field).forEach((linked) => {
        this.cascadeColumnChange(linked.tab, linked.field, column, row, new Set([column]));
      });
      this.syncLinkedRows(field, column, row);
      // Split dulu (kolom `follow` baris pertama disalin ke baris hasil split), baru Alternative UoM membaca nilai terakhir.
      this.applySplitRows(tab, field, column, row);
      this.syncCompanionRows(tab, field);
      this.syncAltUomFromColumns(field, column);
      this.applyLockedValues();
      this.applyRowAutomation(tab, field, column, row);
      this.applyWatchedRowAutofill(field);
    },
    // Entry `row_autofill` yang `watch_rows`-nya memuat `changedField` (tabel LAIN yang dibaca lewat bagian join `{ rows }`,
    // mis. Profit Center INALUM membaca Valuation Class tabel Accounting): dijalankan ulang untuk semua baris tabelnya
    // setelah ada kolom / baris tabel itu yang berubah. Generic per config.
    applyWatchedRowAutofill(changedField) {
      if (this.disabled) return;
      Object.keys(this.formConfig?.tab_fields || {}).forEach((tab) => {
        Object.entries(formConfigHelper.getTabFieldMap(this.formConfig, tab) || {}).forEach(([field, fc]) => {
          const entries = (fc?.row_autofill || []).filter((entry) => [].concat(entry?.watch_rows || []).includes(changedField));
          if (!entries.length) return;
          (Array.isArray(this.mcrItem?.[field]) ? this.mcrItem[field] : []).forEach((row) => {
            entries.forEach((entry) => this.runRowAutofill(tab, field, entry, row, false));
          });
        });
      });
    },
    // ===== Otomasi baris dari config (FORM_CONFIG_SCHEMA.md `row_autofill`, `auto_select_single` kolom) =====
    // Setelah `column` di `row` berubah: entry `row_autofill` yang trigger-nya memuat `column` dijalankan (nilai kolom tujuan
    // ditimpa), lalu kolom ber-`auto_select_single` yang kosong dicoba dipilih otomatis -- untuk field ini dan field yang
    // barisnya objek yang sama (`rows_from`). Mode lihat-saja dilewati.
    applyRowAutomation(tab, field, column, row) {
      if (this.disabled) return;
      this.linkedTableFields(tab, field).forEach((linked) => {
        const fc = formConfigHelper.getFieldConfig(this.formConfig, linked.tab, linked.field);
        (fc?.row_autofill || []).forEach((entry) => {
          if ([].concat(entry?.trigger || []).includes(column)) this.runRowAutofill(linked.tab, linked.field, entry, row, false);
        });
        this.autoSelectRowColumns(linked.tab, linked.field, row);
      });
    },
    // Semua entry `row_autofill` field ini dijalankan untuk satu baris (yang kolom triggernya sudah terisi).
    runRowAutofillEntries(tab, field, row) {
      if (this.disabled) return;
      (formConfigHelper.getFieldConfig(this.formConfig, tab, field)?.row_autofill || [])
        .forEach((entry) => this.runRowAutofill(tab, field, entry, row, false));
    },
    // Satu entry `row_autofill`: semua `trigger` terisi (ctx baris) -> request `endpoint`/`params` (bahasa param sama dengan
    // data_source), record = `record_path` di data response; kolom `columns` ({ kolom_tabel: path_di_record }) diisi dari
    // record. Tidak ada record -> tidak mengubah apa pun. `onlyEmpty` (saat item dibuka): cuma mengisi kolom yang masih kosong.
    async runRowAutofill(tab, field, entry, row, onlyEmpty) {
      if (this.disabled || !entry?.endpoint) return;
      // `when` (opsional, ctx baris): entry dilewati kalau tidak cocok (mis. MRP INALUM ZNNV/ZNOV -- kolom terkunci).
      if (entry.when && !formConfigHelper.evaluateCondition(entry.when, this.buildRowCtx(row))) return;
      const triggers = [].concat(entry.trigger || []);
      const ready = () => triggers.every((name) => formConfigHelper.isFilledValue(this.buildRowCtx(row).getField(name)));
      if (!ready()) return;
      const resolved = formConfigHelper.resolveEndpoint(entry.endpoint, formConfigHelper.resolveDataSourceParams(entry, this.buildRowCtx(row)));
      if (!resolved.ready || Object.values(resolved.params).some((v) => v === undefined)) return;
      const cacheKey = `${resolved.url}?${JSON.stringify(Object.keys(resolved.params).sort().map((k) => [k, resolved.params[k]]))}`;
      if (!AUTOFILL_CACHE.has(cacheKey)) {
        AUTOFILL_CACHE.set(cacheKey, api(this, apiHeader.get(resolved.url.replace(/^\/api\//, ""), { params: resolved.params }), false)
          .then((resp) => resp?.data)
          .catch(() => { AUTOFILL_CACHE.delete(cacheKey); return null; }));
      }
      const data = await AUTOFILL_CACHE.get(cacheKey);
      const record = entry.record_path ? this.resolveSourcePath(data, entry.record_path) : data;
      const hasRecord = !!record && typeof record === "object";
      // `overrides` (opsional): [{ when, values: { kolom: nilai } }] -- entry pertama yang `when`-nya cocok (ctx baris, boleh
      // context FE `user_section`) menimpa hasil record, mis. MRP Controller INALUM per section user. Tetap berlaku walau
      // record tidak ditemukan.
      const override = (entry.overrides || []).find((o) => !o?.when || formConfigHelper.evaluateCondition(o.when, this.buildRowCtx(row)));
      if (!hasRecord && !override) return;
      // Pemicu sudah dikosongkan selama request berjalan -> hasil dibuang.
      if (!ready()) return;
      const assignments = hasRecord
        ? Object.entries(entry.columns || {}).map(([column, path]) => [column, this.resolveSourcePath(record, path)])
        : [];
      Object.entries(override?.values || {}).forEach(([column, value]) => assignments.push([column, value]));
      const changed = [];
      assignments.forEach(([column, value]) => {
        if (onlyEmpty && formConfigHelper.isFilledValue(row[column])) return;
        const next = value === undefined || value === null ? "" : value;
        if (String(row[column] ?? "") === String(next)) return;
        row[column] = next;
        if (!changed.includes(column)) changed.push(column);
      });
      // Kolom yang diisi ikut menjalankan aturan kolom lain di baris itu (depends_on / derived), tanpa memicu autofill lagi.
      // `derived` + `attribute` membaca opsi kolom sumber -> opsinya dimuat dulu (mis. Service Level dari MRP Controller).
      const columns = formConfigHelper.getFieldConfig(this.formConfig, tab, field)?.columns || {};
      const derivedSources = new Set(Object.values(columns).flatMap((col) => (col?.default_value || [])
        .filter((e) => e?.type === "derived" && e.attribute).map((e) => e.from)));
      await Promise.all(changed.filter((column) => derivedSources.has(column)).map((column) => this.loadColumnOptions(tab, field, column, row)));
      changed.forEach((column) => this.cascadeColumnChange(tab, field, column, row, new Set([column, ...Object.keys(entry.columns || {})])));
      // Kolom yang diisi autofill juga memicu `sync_rows` (mis. Warehouse Number terisi -> baris Storage Type dibuat).
      changed.forEach((column) => this.syncLinkedRows(field, column, row));
      if (changed.length) this.applyLockedValues();
    },
    // Kolom ber-`auto_select_single` (boolean / condition ctx baris) yang kosong, tampil & dependency-nya siap: opsinya dimuat,
    // kalau cuma satu langsung dipilih -- lalu diperlakukan seperti dipilih user (onColumnChange: autofill kolom lain).
    autoSelectRowColumns(tab, field, row) {
      const columns = formConfigHelper.getFieldConfig(this.formConfig, tab, field)?.columns || {};
      Object.entries(columns).forEach(([column, colConfig]) => {
        if (colConfig?.auto_select_single) this.autoSelectSingleColumn(tab, field, column, row);
      });
    },
    async autoSelectSingleColumn(tab, field, column, row) {
      if (this.disabled || formConfigHelper.isFilledValue(row[column])) return;
      const rule = formConfigHelper.getColumnConfig(this.formConfig, tab, field, column)?.auto_select_single;
      const ctx = this.buildRowCtx(row);
      if (!rule || (rule !== true && !formConfigHelper.evaluateCondition(rule, ctx))) return;
      if (!formConfigHelper.isColumnVisible(this.formConfig, tab, field, column, ctx)) return;
      if (!formConfigHelper.isColumnDependencyReady(this.formConfig, tab, field, column, ctx)) return;
      await this.loadColumnOptions(tab, field, column, row);
      const options = this.dialog.options[this.columnOptionsKey(tab, field, column, row)] || [];
      if (options.length !== 1 || formConfigHelper.isFilledValue(row[column])) return;
      row[column] = options[0].value;
      this.onColumnChange(tab, field, column, row);
    },
    // Saat item dibuka (bukan mode lihat-saja): kolom ber-auto_select_single yang kosong dipilih, lalu `row_autofill` cuma ke
    // kolom yang masih kosong (nilai tersimpan tidak ditimpa).
    applyRowAutomationOnOpen(tab) {
      if (this.disabled) return;
      Object.entries(formConfigHelper.getTabFieldMap(this.formConfig, tab) || {}).forEach(([field, fc]) => {
        if (!fc?.columns) return;
        (Array.isArray(this.mcrItem?.[field]) ? this.mcrItem[field] : []).forEach((row) => {
          this.autoSelectRowColumns(tab, field, row);
          (fc.row_autofill || []).forEach((entry) => this.runRowAutofill(tab, field, entry, row, true));
        });
      });
    },
    // Pemicu baris yang berupa field LEVEL FORM (bukan kolom tabel, mis. cat_material_type/cat_material_group di Basic Data untuk
    // MRP INALUM): diawasi selama tab ini hidup. Berubah -> di tiap baris: kolom yang depends_on field itu dikosongkan (berantai),
    // entry row_autofill yang trigger-nya memuat field itu dijalankan, kolom auto_select_single kosong dipilih ulang.
    watchExternalRowTriggers(tab) {
      EXTERNAL_WATCH.get(this)?.();
      EXTERNAL_WATCH.delete(this);
      if (this.disabled) return;
      const names = new Set();
      Object.values(formConfigHelper.getTabFieldMap(this.formConfig, tab) || {}).forEach((fc) => {
        if (!fc?.columns) return;
        const columnNames = new Set(Object.keys(fc.columns));
        const add = (name) => { if (name && !columnNames.has(name)) names.add(name); };
        Object.values(fc.columns).forEach((col) => (Array.isArray(col?.depends_on) ? col.depends_on : []).forEach(add));
        // Default kolom yang `when`-nya merujuk field level form (mis. Requirement Group INALUM dari Material Type) dihitung ulang.
        Object.values(fc.columns).forEach((col) => (col?.default_value || []).forEach((e) => {
          if (e?.when) formConfigHelper.getConditionFields(e.when).forEach(add);
        }));
        (fc.row_autofill || []).forEach((entry) => [].concat(entry?.trigger || []).forEach(add));
      });
      if (!names.size) return;
      const list = [...names];
      // Diikat ke item yang sedang dibuka: pindah item tidak dianggap "field berubah" (onItemBound memasang pengawas baru).
      const item = this.mcrItem;
      const unwatch = this.$watch(() => list.map((name) => item?.[name] ?? ""), (now, before) => {
        if (this.mcrItem !== item || this.disabled) return;
        list.forEach((name, i) => {
          if (String(now[i]) === String(before?.[i])) return;
          Object.entries(formConfigHelper.getTabFieldMap(this.formConfig, tab) || {}).forEach(([field, fc]) => {
            if (!fc?.columns) return;
            (Array.isArray(item?.[field]) ? item[field] : []).forEach((row) => {
              this.cascadeColumnChange(tab, field, name, row, new Set([name]));
              this.applyRowAutomation(tab, field, name, row);
            });
          });
        });
        // Kolom terkunci yang default-nya bergantung field ini (mis. MRP INALUM ZNNV/ZNOV) ditulis ulang setelah cascade.
        this.applyLockedValues();
        // Kolom `follow` pemicu bisa ikut berubah (mis. Valuation Category ANTAM "Z" dari Material Type) -> pendamping disalin ulang.
        this.syncAllCompanionRows(tab);
      });
      EXTERNAL_WATCH.set(this, unwatch);
    },
    // Field biasa (tidak terkunci, bukan tabel) di tab ini yang default_value-nya ber-`when`: field yang dirujuk `when` diawasi;
    // berubah (oleh user, pilih otomatis, lookup) -> nilai default yang cocok saat itu ditulis ulang (static), mis. Batch
    // Management INALUM dari Material Type. User tetap bisa mengubah sesudahnya. Pindah item tidak dianggap perubahan; mode
    // lihat-saja dilewati. Field terkunci (`readonlyandsaved`) sudah dihitung applyLockedValues.
    watchFieldDefaultSources(tab) {
      DEFAULT_WATCH.get(this)?.();
      DEFAULT_WATCH.delete(this);
      if (this.disabled) return;
      const targets = Object.entries(formConfigHelper.getTabFieldMap(this.formConfig, tab) || {})
        .filter(([, fc]) => fc && !fc.columns && !fc.readonlyandsaved && (fc.default_value || []).some((e) => e?.when));
      if (!targets.length) return;
      const sources = [...new Set(targets.flatMap(([, fc]) => fc.default_value
        .flatMap((e) => (e?.when ? formConfigHelper.getConditionFields(e.when) : []))))];
      const item = this.mcrItem;
      const unwatch = this.$watch(() => sources.map((name) => this.formConfigCtx.getField(name) ?? ""), (now, before) => {
        if (this.mcrItem !== item || this.disabled) return;
        const changed = sources.filter((name, i) => String(now[i]) !== String(before?.[i]));
        targets.forEach(([field, fc]) => {
          const refs = fc.default_value.flatMap((e) => (e?.when ? formConfigHelper.getConditionFields(e.when) : []));
          if (!refs.some((name) => changed.includes(name)) || !this.isFieldVisibleFn(field, tab)) return;
          const value = resolveColumnDefault(formConfigHelper.resolveActiveDefault(this.formConfig, tab, field, this.formConfigCtx));
          if (value !== undefined) item[field] = value;
        });
      });
      DEFAULT_WATCH.set(this, unwatch);
    },
    // Field tabel yang memakai baris `field`: field itu sendiri + field (tab mana pun) yang `rows_from`-nya `field`.
    linkedTableFields(tab, field) {
      const linked = [{ tab, field }];
      Object.keys(this.formConfig?.tab_fields || {}).forEach((otherTab) => {
        Object.entries(formConfigHelper.getTabFieldMap(this.formConfig, otherTab) || {}).forEach(([otherField, fc]) => {
          if (fc?.columns && fc.rows_from === field) linked.push({ tab: otherTab, field: otherField });
        });
      });
      return linked;
    },
    cascadeColumnChange(tab, field, column, row, visited) {
      const columns = formConfigHelper.getFieldConfig(this.formConfig, tab, field)?.columns || {};
      Object.entries(columns).forEach(([target, colConfig]) => {
        if (visited.has(target)) return;
        let value;
        if (Array.isArray(colConfig?.depends_on) && colConfig.depends_on.includes(column)) value = "";
        const entry = formConfigHelper.resolveActiveColumnDefault(this.formConfig, tab, field, target, this.buildRowCtx(row));
        // default_value yang `when`-nya merujuk kolom ini dihitung ulang (static / relative_date), mis. Valid Until: Type Z1/Z2
        // -> hari ini, Type lain -> 9999-12-31.
        if ((colConfig?.default_value || []).some((e) => e?.when && formConfigHelper.getConditionFields(e.when).includes(column))) {
          const recomputed = resolveColumnDefault(entry);
          if (recomputed !== undefined) value = recomputed;
        }
        if (entry?.type === "derived" && entry.from === column) value = this.resolveDerivedColumnValue(tab, field, entry, row);
        if (value === undefined) return;
        visited.add(target);
        row[target] = value;
        this.cascadeColumnChange(tab, field, target, row, visited);
      });
    },
    // default_value `derived` kolom: nilai kolom `from` di baris yang sama, atau -- kalau ada `attribute` -- atribut OPSI
    // yang sedang dipilih di kolom `from` (opsi membawa atribut itu lewat data_source.extra_fields kolom `from`).
    // Tidak ada opsi / atribut kosong -> null (mis. MRP Controller tanpa service level).
    resolveDerivedColumnValue(tab, field, entry, row) {
      const value = row[entry.from];
      if (!entry.attribute) return value ?? null;
      if (!formConfigHelper.isFilledValue(value)) return null;
      const options = this.dialog.options[this.columnOptionsKey(tab, field, entry.from, row)] || [];
      const selected = options.find((option) => String(option.value) === String(value));
      return selected?.[entry.attribute] ?? null;
    },
    // Saat item dibuka, sel tabel yang MASIH KOSONG diisi dari default_value kolomnya:
    // - `derived` (kolom `from` terisi): aturan yang sama seperti onColumnChange (mis. Service Level kosong di DB padahal
    //   MRP Controller MRS/SPP -- tanpa ini kolom read-only itu tetap kosong & Save tertahan);
    // - static / relative_date yang entry-nya bertanda `fill_empty: true` (mis. Type Manufacture INALUM MCR001 "VALID" untuk
    //   baris lama yang Type-nya kosong). Default tanpa tanda itu tetap cuma untuk baris baru (addRow).
    // Nilai yang sudah ada TIDAK ditimpa. Mode lihat-saja dilewati (tampilkan data apa adanya). Generic per config.
    // Panggil SEBELUM preloadTabLabels: request opsi kolom `from` dimulai di sini, preload melihat kuncinya sedang dimuat
    // lalu melewatinya (satu request per kunci).
    async fillEmptyColumnDefaults(tab) {
      if (this.disabled) return;
      const jobs = [];
      Object.entries(formConfigHelper.getTabFieldMap(this.formConfig, tab) || {}).forEach(([field, fc]) => {
        if (!fc?.columns) return;
        (Array.isArray(this.mcrItem?.[field]) ? this.mcrItem[field] : []).forEach((row) => {
          Object.keys(fc.columns).forEach((column) => {
            if (formConfigHelper.isFilledValue(row[column])) return;
            const entry = formConfigHelper.resolveActiveColumnDefault(this.formConfig, tab, field, column, this.buildRowCtx(row));
            if (entry?.fill_empty) {
              const value = resolveColumnDefault(entry);
              if (value !== undefined) row[column] = value;
              return;
            }
            if (entry?.type !== "derived" || !formConfigHelper.isFilledValue(row[entry.from])) return;
            jobs.push({ field, column, entry, row });
          });
        });
      });
      this.applyLockedValues();
      if (!jobs.length) return;
      // Kunci opsi yang sama dimuat sekali (panggilan berikutnya langsung kembali, Promise.all tetap menunggu yang pertama).
      await Promise.all(jobs.filter((job) => job.entry.attribute).map((job) => this.loadColumnOptions(tab, job.field, job.entry.from, job.row)));
      jobs.forEach(({ field, column, entry, row }) => {
        if (formConfigHelper.isFilledValue(row[column])) return;
        const value = this.resolveDerivedColumnValue(tab, field, entry, row);
        if (value !== null && value !== undefined) row[column] = value;
      });
    },
    // ===== `rows_source` (FORM_CONFIG_SCHEMA.md): baris tabel dibuat dari daftar API =====
    // Mis. Characteristic & Value: satu baris per characteristic Item Name + Item Type terpilih. Dibuat ulang saat field
    // `trigger` diubah (onFieldChange) dan saat item dibuka dengan tabel kosong (fillEmptySourcedRows). Baris yang sudah
    // ada digabung per kolom `key` (semua baris ber-key sama dipertahankan, termasuk baris tambahan characteristic
    // multiple); kolom hasil pemetaan `columns` diperbarui dari daftar. Key yang tidak ada lagi di daftar -> barisnya keluar
    // dari tabel (disimpan di cache runtime, kembali kalau trigger dikembalikan). Trigger kosong -> tabel kosong.
    sourcedTableFields(trigger) {
      const fields = [];
      Object.keys(this.formConfig?.tab_fields || {}).forEach((tab) => {
        Object.entries(formConfigHelper.getTabFieldMap(this.formConfig, tab) || {}).forEach(([field, fc]) => {
          const source = fc?.columns && fc.rows_source;
          if (source && (trigger === undefined || [].concat(source.trigger || []).includes(trigger))) fields.push({ tab, field });
        });
      });
      return fields;
    },
    // Nilai satu kolom dari satu record daftar: path (boleh dot-path) atau { source_field, map, default } -- `map` menerjemahkan
    // nilai mentah (mis. single_or_multiple "multiple" -> is_multiple "YES"), nilai di luar `map` -> `default`.
    resolveSourcedColumn(record, mapping) {
      if (mapping && typeof mapping === "object" && !Array.isArray(mapping)) {
        const raw = String(this.resolveSourcePath(record, mapping.source_field) ?? "");
        if (mapping.map && Object.prototype.hasOwnProperty.call(mapping.map, raw)) return mapping.map[raw];
        return mapping.default !== undefined ? mapping.default : this.resolveSourcePath(record, mapping.source_field);
      }
      return this.resolveSourcePath(record, mapping);
    },
    async refreshSourcedRows(tab, field) {
      const source = formConfigHelper.getFieldConfig(this.formConfig, tab, field)?.rows_source;
      if (this.disabled || !source?.endpoint || !source.key || !this.isTableVisible(tab, field)) return;
      const item = this.mcrItem;
      const token = {};
      rawStore(SOURCE_TOKEN, item).set(field, token);
      const cacheByField = rawStore(SOURCE_CACHE, item);
      if (!cacheByField.has(field)) cacheByField.set(field, new Map());
      const cache = cacheByField.get(field);
      const remember = (rows) => {
        const byKey = new Map();
        (Array.isArray(rows) ? rows : []).forEach((row) => {
          const key = String(row[source.key] ?? "");
          if (!key) return;
          if (!byKey.has(key)) byKey.set(key, []);
          byKey.get(key).push(row);
        });
        byKey.forEach((keyRows, key) => cache.set(key, keyRows));
      };
      remember(item[field]);
      const triggers = [].concat(source.trigger || []);
      if (!triggers.every((name) => formConfigHelper.isFilledValue(this.formConfigCtx.getField(name)))) {
        item[field] = [];
        return;
      }
      const resolved = formConfigHelper.resolveEndpoint(source.endpoint, formConfigHelper.resolveDataSourceParams(source, this.formConfigCtx));
      if (!resolved.ready) return;
      let list;
      try {
        const resp = await api(this, apiHeader.get(resolved.url.replace(/^\/api\//, ""), { params: resolved.params }), false);
        list = Array.isArray(resp?.data?.data) ? resp.data.data : Array.isArray(resp?.data) ? resp.data : [];
      } catch (error) {
        return;
      }
      if (this.mcrItem !== item || SOURCE_TOKEN.get(toRaw(item))?.get(field) !== token) return;
      // Baris yang diedit selama fetch ikut dipertahankan.
      remember(item[field]);
      const rows = [];
      list.forEach((record) => {
        const mapped = Object.fromEntries(Object.entries(source.columns || {}).map(([column, mapping]) => [column, this.resolveSourcedColumn(record, mapping)]));
        const existing = cache.get(String(mapped[source.key] ?? ""));
        if (existing?.length) {
          existing.forEach((row) => Object.assign(row, mapped));
          rows.push(...existing);
        } else {
          rows.push({ ...this.buildNewRow(tab, field), ...mapped });
        }
      });
      item[field] = rows;
      this.applyLockedValues();
      this.preloadColumnLabels(tab, field);
    },
    // Saat item dibuka: tabel ber-`rows_source` milik tab yang masih kosong dibuat dari daftar (data lama / item baru).
    // Tabel yang sudah berisi tidak disentuh. Mode lihat-saja dilewati.
    fillEmptySourcedRows(tab) {
      if (this.disabled) return;
      this.sourcedTableFields().filter((sourced) => sourced.tab === tab).forEach(({ field }) => {
        const rows = this.mcrItem?.[field];
        if (!Array.isArray(rows) || !rows.length) this.refreshSourcedRows(tab, field);
      });
    },
    // `row_actions.duplicate` (default tidak boleh, dievaluasi dengan ctx BARIS): tambah baris tepat di bawah `row`, mis.
    // characteristic multiple. Kolom pemetaan `rows_source` (cin_code, nama, is_mandatory, ...) disalin, kolom lain kosong.
    canDuplicateRow(tab, field, row) {
      return formConfigHelper.isRowActionAllowed(this.formConfig, tab, field, "duplicate", this.buildRowCtx(row), false);
    },
    duplicateRow(tab, field, row) {
      const rows = this.mcrItem[field] || [];
      const source = formConfigHelper.getFieldConfig(this.formConfig, tab, field)?.rows_source;
      const newRow = this.buildNewRow(tab, field);
      Object.keys(source?.columns || {}).forEach((column) => { newRow[column] = row[column]; });
      rows.splice(rows.indexOf(row) + 1, 0, newRow);
      this.applyLockedValues();
    },
    // Hapus satu baris: `row_actions.remove` + tabel ber-`rows_source` minimal menyisakan satu baris per key (baris dari
    // daftar tidak bisa hilang, cuma baris tambahannya).
    canRemoveTableRow(tab, field, row) {
      if (!this.canRemoveRow(tab, field)) return false;
      const key = formConfigHelper.getFieldConfig(this.formConfig, tab, field)?.rows_source?.key;
      if (!key) return true;
      return (this.mcrItem[field] || []).some((other) => other !== row && String(other[key] ?? "") === String(row[key] ?? ""));
    },
    // Kolom wajib di BARIS ini (rules `required` + `when` dengan ctx baris, mis. nilai Characteristic wajib kalau
    // is_mandatory YES) -- sama dengan validasi Save & BE.
    isCellMandatory(tab, field, column, row) {
      return formConfigHelper.isColumnMandatory(this.formConfig, tab, field, column, this.buildRowCtx(row));
    },
    // ===== Kolom teks bebas + saran (AutoComplete), mis. nilai Characteristic =====
    // Saran dari data_source kolom (dicari ke server, digabung ke opsi kunci itu); teks yang tidak dipilih dari saran
    // disimpan apa adanya (BE membuat nilai baru). Daftar saran satu untuk tab (cuma satu panel terbuka sekaligus) --
    // tab menyediakan `dialog.suggestions` (createTabDialog extra).
    async searchColumnSuggestions(tab, field, column, row, event) {
      const query = String(event?.query ?? "").trim();
      const optionsKey = this.columnOptionsKey(tab, field, column, row);
      await this.loadSelectFieldOptions(field, { tab, column, row, optionsKey, force: true, search: query, merge: true });
      const lower = query.toLowerCase();
      this.dialog.suggestions = (this.dialog.options[optionsKey] || [])
        .filter((option) => !lower || String(option.label ?? "").toLowerCase().includes(lower));
    },
    // Teks yang tampil: label opsi yang cocok dengan nilai baris, selain itu nilainya sendiri (teks bebas).
    columnTextValue(tab, field, column, row) {
      const value = row?.[column];
      if (!formConfigHelper.isFilledValue(value)) return "";
      const option = (this.dialog.options[this.columnOptionsKey(tab, field, column, row)] || []).find((o) => String(o.value) === String(value));
      return option ? option.label : String(value);
    },
    onColumnSuggestionSelect(tab, field, column, row, event) {
      row[column] = event?.value?.value ?? "";
      this.onColumnChange(tab, field, column, row);
    },
    // Diketik: disimpan sebagai teks bebas -- kecuali sama persis (tanpa beda huruf besar/kecil) dengan label saran yang
    // sudah dimuat, maka kode opsi itu yang disimpan (tidak membuat nilai baru kembar di BE).
    onColumnTextInput(tab, field, column, row, value) {
      if (value && typeof value === "object") return;
      const text = String(value ?? "");
      const lower = text.trim().toLowerCase();
      const option = lower
        ? (this.dialog.options[this.columnOptionsKey(tab, field, column, row)] || []).find((o) => String(o.label ?? "").trim().toLowerCase() === lower)
        : null;
      row[column] = option ? option.value : text;
    },
    // Kolom angka: nilai dari DB sering berupa string ("180", "0.000"); InputNumber butuh number (kosong -> null).
    toNumberValue(value) {
      if (value === null || value === undefined || value === "") return null;
      const number = Number(value);
      return Number.isNaN(number) ? null : number;
    },
    // Kolom tanggal: item menyimpan "YYYY-MM-DD" (default) atau "dd/mm/yyyy HH:ii:ss" (kolom yang memang disimpan begitu di
    // DB, mis. review_date_inalum_data Data Satu); Calendar memakai Date. Format simpan = urusan layout kolom di tab.
    toDateValue(value) {
      if (!value) return null;
      if (value instanceof Date) return value;
      const dmy = String(value).match(/^(\d{2})\/(\d{2})\/(\d{4})/);
      if (dmy) return new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));
      const [y, m, d] = String(value).slice(0, 10).split("-").map(Number);
      return y ? new Date(y, (m || 1) - 1, d || 1) : null;
    },
    fromDateValue(date, store) {
      if (!(date instanceof Date)) return "";
      if (store === "dmy_time") {
        const pad = (n) => String(n).padStart(2, "0");
        return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} 00:00:00`;
      }
      return formConfigHelper.formatDate(date);
    },
    // Field biasa invalid = ada error Save untuk field itu & nilainya masih kosong.
    hasErrorField(field) {
      const err = this.setErrorValidCat?.[this.selectedMcrItemIndex];
      if (!err?.[field]) return false;
      const value = this.mcrItem?.[field];
      return value === "" || value === null || value === undefined;
    },
    delCertErrorVal(field) {
      if (!this.setErrorValidCat?.[this.selectedMcrItemIndex]) return;
      this.deleteCertainErrorValidation({
        formUsedFor: "cataloguer",
        field,
        idxMcrItem: this.selectedMcrItemIndex,
        subTab: "",
        indexRowTab: null
      });
    }
  }
};
